import { useState, useEffect, useCallback } from "react";
import {
  Users, CalendarOff, Banknote,
  UserCheck, UserX, AlertTriangle, Loader2,
  Activity,
} from "lucide-react";
import Panel from "../../components/Panel";
import StatCard from "../../components/StatCard";
import { Link } from "react-router-dom";
import { businessApi } from "../../../services/api";
import { PageHeader } from "../../../components/ui";

const fmtCurrency = (cents) => {
  if (cents == null) return "₱0.00";
  return `₱${(Number(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";

const STATUS_STYLES = {
  pending:    "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  approved:   "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  rejected:   "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  cancelled:  "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400",
};

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function HRDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    suspendedEmployees: 0,
    terminatedEmployees: 0,
    pendingLeave: [],
    latestPayroll: null,
    latestPayrollPayslips: [],
    attendanceToday: null,
    onLeaveToday: [],
    lowBalanceAlerts: [],
    recentActivity: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = todayISO();
      const [membersRes, leaveRes, payrollRes, attendanceRes, onLeaveTodayRes, activityRes] = await Promise.all([
        businessApi.getMembers().catch(() => ({ members: [], data: [] })),
        businessApi.getLeaveRequests({ status: "pending", limit: 10 }).catch(() => ({ rows: [] })),
        businessApi.getPayrollPeriods({ limit: 1 }).catch(() => ({ rows: [] })),
        businessApi.getAttendanceSummary({ dateFrom: today, dateTo: today }).catch(() => null),
        businessApi.getLeaveRequests({ status: "approved", limit: 50 }).catch(() => ({ rows: [] })),
        businessApi.getHRActivityLogs({ limit: 8 }).catch(() => ({ rows: [], data: [] })),
      ]);

      const members = membersRes.members || membersRes.data || [];
      const active = members.filter(m => (m.status || "").toUpperCase() === "ACTIVE").length;
      const suspended = members.filter(m => (m.status || "").toUpperCase() === "SUSPENDED").length;
      const terminated = members.filter(m => ["TERMINATED", "LEFT"].includes((m.status || "").toUpperCase())).length;

      let latestPayroll = (payrollRes.rows || [])[0] || null;
      let latestPayrollPayslips = [];

      if (latestPayroll) {
        try {
          const psRes = await businessApi.getPayslips(latestPayroll.id);
          latestPayrollPayslips = psRes.rows || [];
        } catch { /* ignore */ }
      }

      // Filter on-leave-today from approved requests
      const allApproved = onLeaveTodayRes.rows || [];
      const onLeaveToday = allApproved.filter((r) => {
        const s = r.start_date?.slice(0, 10);
        const e = r.end_date?.slice(0, 10);
        return s && e && s <= today && e >= today;
      });

      setStats({
        totalEmployees: members.length,
        activeEmployees: active,
        suspendedEmployees: suspended,
        terminatedEmployees: terminated,
        pendingLeave: leaveRes.rows || [],
        latestPayroll,
        latestPayrollPayslips,
        attendanceToday: attendanceRes,
        onLeaveToday,
        lowBalanceAlerts: [],
        recentActivity: activityRes.rows || activityRes.data || [],
      });
    } catch (err) {
      console.error("Dashboard load failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  const totalPayrollGross = stats.latestPayrollPayslips
    .filter(p => p.status !== "voided")
    .reduce((s, p) => s + Number(p.gross_pay_cents || 0), 0);
  const totalPayrollNet = stats.latestPayrollPayslips
    .filter(p => p.status !== "voided")
    .reduce((s, p) => s + Number(p.net_pay_cents || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader title="HR Dashboard" description="Quick overview of workforce, leave, and payroll." />

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Employees" value={stats.totalEmployees} icon={Users} to="/business/hr/employees" />
        <StatCard title="Active" value={stats.activeEmployees} icon={UserCheck} to="/business/hr/employees" />
        <StatCard title="Suspended" value={stats.suspendedEmployees} icon={AlertTriangle} accent={stats.suspendedEmployees > 0} />
        <StatCard title="Terminated / Left" value={stats.terminatedEmployees} icon={UserX} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pending Leave Requests */}
        <Panel title="Pending Leave Requests" right={<Link to="/business/hr/open-work?lane=leave" className="text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-neutral-100">Open work</Link>}>
          <div className="flex items-center gap-2 mb-3">
            <CalendarOff className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium dark:text-neutral-100">{stats.pendingLeave.length} pending</span>
          </div>
          {stats.pendingLeave.length === 0 ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400 py-4">No pending leave requests.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs font-medium uppercase text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
                    <th className="px-2 py-1.5">Employee</th>
                    <th className="px-2 py-1.5">Type</th>
                    <th className="px-2 py-1.5">Dates</th>
                    <th className="px-2 py-1.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-neutral-700">
                  {stats.pendingLeave.slice(0, 3).map((r) => (
                    <tr key={r.id}>
                      <td className="px-2 py-1.5 dark:text-neutral-300">{r.employee_name || `#${r.business_member_id}`}</td>
                      <td className="px-2 py-1.5 dark:text-neutral-300">{r.leave_type_name || `Type #${r.leave_type_id}`}</td>
                      <td className="px-2 py-1.5 dark:text-neutral-300 whitespace-nowrap">{fmtDate(r.start_date)} — {fmtDate(r.end_date)}</td>
                      <td className="px-2 py-1.5">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[r.status] || STATUS_STYLES.pending}`}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {/* Latest Payroll Period */}
        <Panel title="Latest Payroll Period">
          {!stats.latestPayroll ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400 py-4">No payroll periods created yet.</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Banknote className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-semibold dark:text-neutral-100">{stats.latestPayroll.name}</span>
                <span className={`ml-auto inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  stats.latestPayroll.status === "paid" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" :
                  stats.latestPayroll.status === "closed" ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400" :
                  stats.latestPayroll.status === "processing" ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" :
                  "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                }`}>{stats.latestPayroll.status}</span>
              </div>

              <div className="text-xs text-neutral-500 dark:text-neutral-400">
                {fmtDate(stats.latestPayroll.start_date)} — {fmtDate(stats.latestPayroll.end_date)}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-2 text-center">
                  <div className="text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Payslips</div>
                  <div className="text-lg font-bold dark:text-neutral-50">{stats.latestPayrollPayslips.filter(p => p.status !== "voided").length}</div>
                </div>
                <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-2 text-center">
                  <div className="text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Gross</div>
                  <div className="text-sm font-bold dark:text-neutral-50">{fmtCurrency(totalPayrollGross)}</div>
                </div>
                <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-2 text-center">
                  <div className="text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Net</div>
                  <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{fmtCurrency(totalPayrollNet)}</div>
                </div>
              </div>
            </div>
          )}
        </Panel>
      </div>

      {/* Today's Attendance + On Leave Today */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Today's Attendance Summary */}
        <Panel title="Today's Attendance">
          {!stats.attendanceToday ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400 py-4">No attendance data for today.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {[
                { label: "Present", val: stats.attendanceToday.present || 0, color: "text-emerald-600 dark:text-emerald-400" },
                { label: "Late", val: stats.attendanceToday.late || 0, color: "text-amber-600 dark:text-amber-400" },
                { label: "Absent", val: stats.attendanceToday.absent || 0, color: "text-red-600 dark:text-red-400" },
                { label: "On Leave", val: stats.attendanceToday.on_leave || 0, color: "text-blue-600 dark:text-blue-400" },
                { label: "Half Day", val: stats.attendanceToday.half_day || 0, color: "text-purple-600 dark:text-purple-400" },
                { label: "Holiday", val: stats.attendanceToday.holiday || 0, color: "text-neutral-600 dark:text-neutral-400" },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-2 text-center">
                  <div className="text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{s.label}</div>
                  <div className={`text-lg font-bold ${s.color}`}>{s.val}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Employees On Leave Today */}
        <Panel title="On Leave Today">
          <div className="flex items-center gap-2 mb-3">
            <CalendarOff className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium dark:text-neutral-100">{stats.onLeaveToday.length} on leave</span>
          </div>
          {stats.onLeaveToday.length === 0 ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400 py-4">No employees on leave today.</div>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {stats.onLeaveToday.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-lg bg-neutral-50 dark:bg-neutral-800 px-3 py-1.5 text-sm">
                  <span className="dark:text-neutral-200">{r.employee_name || `#${r.business_member_id}`}</span>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">{r.leave_type_name || r.leave_type_code}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Recent HR Activity */}
      {stats.recentActivity.length > 0 && (
        <Panel title="Recent HR Activity">
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {stats.recentActivity.map((a) => (
              <div key={a.id} className="flex items-start gap-3 text-sm py-1.5 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                <Activity className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
                <div className="flex-1 min-w-0">
                  <span className="dark:text-neutral-200">{a.description || a.action_type}</span>
                  <div className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                    {a.actor_name || `Member #${a.actor_id || a.business_member_id}`} · {fmtDate(a.created_at)} {fmtTime(a.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
